import { memo, useEffect } from 'react'
import { useLoader } from '@react-three/fiber'
import * as THREE from 'three'
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js'
import { swapMeshUV, applyPBR, unifyRepeatsFromBase, loadTextureMaps } from './textureUtils'

function LegsCmp({
  bedUrl,
  legUrl,
  texture,
  useUvChecker,
  rotateQuarterTurns,
  swapUV,
  legTypeId,
  legSlug,
  bedVariationId,
  isSingleBed,
  edgeInset,
  onRenderReadyChange,
}: {
  bedUrl: string
  legUrl: string
  texture: any
  useUvChecker: boolean
  rotateQuarterTurns: number
  swapUV: boolean
  legTypeId?: number
  legSlug?: string
  bedVariationId?: number
  isSingleBed?: boolean
  edgeInset?: number
  /** Screenshot gate: called once FBX is in memory (after Suspense). */
  onRenderReadyChange?: (ready: boolean) => void
}) {
  const bedBox = (window as any).__bed_bbox__
  if (!bedBox || bedBox.isEmpty()) return null as any

  // For bed bases, legUrl might be null - handle this case
  if (!legUrl) {
    return null
  }
  
  const leg = useLoader(FBXLoader, legUrl)

  // useLoader only resumes this component after the FBX is cached — signal screenshot ready.
  useEffect(() => {
    if (!onRenderReadyChange) return
    onRenderReadyChange(true)
    return () => onRenderReadyChange(false)
  }, [leg, legUrl, onRenderReadyChange])
  const legBox = new THREE.Box3().setFromObject(leg)
  const legCenter = legBox.getCenter(new THREE.Vector3())
  const legMin = legBox.min
  const BASE_SCALE = 0.05
  const legY = -legBox.min.y * BASE_SCALE
  const inset = Number.isFinite(edgeInset) ? Number(edgeInset) : 0.48
  const isFlyingOptics = (legSlug || '').includes('flying-optics')
  const isEiconOnSupremeIconSc26 = (legSlug || '').includes('eicon') && bedVariationId === 567
  const END_OFFSET_CM = 25
  const END_OFFSET = END_OFFSET_CM * BASE_SCALE
  const effectiveLegTypeId = isFlyingOptics
    ? 3
    : isSingleBed && legTypeId === 2
      ? 1
      : legTypeId

  // Determine leg positions based on leg type
  const getLegPositions = () => {
    const bedWidth = bedBox.max.x - bedBox.min.x
    const bedDepth = bedBox.max.z - bedBox.min.z
    const bedCenterX = (bedBox.min.x + bedBox.max.x) / 2
    
    // Base corner positions
    const corners: [number, number][] = [
      [bedBox.min.x, bedBox.max.z], // Front left
      [bedBox.max.x, bedBox.max.z], // Front right
      [bedBox.min.x, bedBox.min.z], // Back left
      [bedBox.max.x, bedBox.min.z], // Back right
    ]
    
    // Additional positions for 8-leg configuration (like two 4-leg beds side by side)
    const additionalPositions: [number, number][] = [
      [bedBox.min.x + bedWidth/2, bedBox.max.z], // Front left inner
      [bedBox.max.x - bedWidth/2, bedBox.max.z], // Front right inner
      [bedBox.min.x + bedWidth/2, bedBox.min.z], // Back left inner
      [bedBox.max.x - bedWidth/2, bedBox.min.z], // Back right inner
    ]
    
    const rotations = [
      [0, -Math.PI/2, 0], // Front left
      [0, 0, 0],          // Front right
      [0, Math.PI, 0],    // Back left
      [0, Math.PI/2, 0],  // Back right
    ]
    
    const additionalRotations = [
      [0, -Math.PI/2, 0], // Front left inner
      [0, 0, 0],          // Front right inner
      [0, Math.PI, 0],    // Back left inner
      [0, Math.PI/2, 0],  // Back right inner
    ]
    
    switch (effectiveLegTypeId) {
      case 1: // 4 Legs
        return {
          positions: corners.map(([x, z], i) => {
            let adjX = x
            let adjZ = z
            if (i === 0 || i === 2) adjX += inset
            if (i === 1 || i === 3) adjX -= inset
            if (i === 0 || i === 1) adjZ -= inset
            if (i === 2 || i === 3) adjZ += inset
            return [adjX, adjZ]
          }),
          rotations
        }
      case 2: // 8 Legs
        return {
          positions: [
            ...corners.map(([x, z], i) => {
              let adjX = x
              let adjZ = z
              if (i === 0 || i === 2) adjX += inset
              if (i === 1 || i === 3) adjX -= inset
              if (i === 0 || i === 1) adjZ -= inset
              if (i === 2 || i === 3) adjZ += inset
              return [adjX, adjZ]
            }),
            ...additionalPositions.map(([x, z], i) => {
              let adjX = x
              let adjZ = z
              if (i === 0 || i === 2) adjX += inset
              if (i === 1 || i === 3) adjX -= inset
              if (i === 0 || i === 1) adjZ -= inset
              if (i === 2 || i === 3) adjZ += inset
              return [adjX, adjZ]
            })
          ],
          rotations: [...rotations, ...additionalRotations]
        }
      case 3: // Bed base - single base model centered under the bed
        if (isFlyingOptics) {
          if (isSingleBed) {
            return {
              positions: [
                [bedCenterX, bedBox.max.z - END_OFFSET],
                [bedCenterX, bedBox.min.z + END_OFFSET],
              ],
              rotations: [[0, 0, 0], [0, 0, 0]]
            }
          }
          const quarterBedWidth = bedWidth / 4
          return {
            positions: [
              [bedCenterX - quarterBedWidth, bedBox.max.z - END_OFFSET],
              [bedCenterX + quarterBedWidth, bedBox.max.z - END_OFFSET],
              [bedCenterX - quarterBedWidth, bedBox.min.z + END_OFFSET],
              [bedCenterX + quarterBedWidth, bedBox.min.z + END_OFFSET],
            ],
            rotations: [[0, 0, 0], [0, 0, 0], [0, 0, 0], [0, 0, 0]]
          }
        }
        return {
          positions: [[0, 0, 0]], // Single position at origin (will be centered under bed)
          rotations: [[0, 0, 0]]  // No rotation needed
        }
      default: // Default to 4 legs
        return {
          positions: corners.map(([x, z], i) => {
            let adjX = x
            let adjZ = z
            if (i === 0 || i === 2) adjX += inset
            if (i === 1 || i === 3) adjX -= inset
            if (i === 0 || i === 1) adjZ -= inset
            if (i === 2 || i === 3) adjZ += inset
            return [adjX, adjZ]
          }),
          rotations
        }
    }
  }

  const { positions, rotations } = getLegPositions()


  return (
    <>
      {positions.map((position, i) => {
        // Keep corner log minimal
        // debug: corner index
        const clone = leg.clone()
        clone.scale.setScalar(BASE_SCALE)
        
        // Use same positioning for all leg types (including bed base) - position is adjusted later via group
        clone.position.set(-legCenter.x * BASE_SCALE, -legMin.y * BASE_SCALE, -legCenter.z * BASE_SCALE)
        
        clone.updateMatrixWorld(true)

        if (texture?.base_color_url || texture?.base_color_urls) {
          clone.traverse((child: any) => {
            if (child.isMesh && child.material) {
              try {
                child.geometry = (child.geometry as THREE.BufferGeometry).clone()
                if (child.geometry && child.geometry.userData) {
                  // Clear any existing UV cache keys to ensure fresh state
                  Object.keys(child.geometry.userData).forEach(key => {
                    if (key.startsWith('_origUV_')) {
                      delete child.geometry.userData[key]
                    }
                  })
                }
              } catch {}
              // Apply UV swap same way for all leg types (including bed base)
              // Use mesh name in cache key to ensure uniqueness per mesh (like tables do)
              swapMeshUV(child, swapUV, `legs-${i}-${child.name || 'mesh'}`)
              const mat = new THREE.MeshStandardMaterial()
              mat.color = new THREE.Color(0xffffff)
              ;(mat as any).emissive?.setRGB?.(0, 0, 0)
              ;(mat as any).vertexColors = false
              // Use same rotation logic for all leg types (including bed base)
              const turns: number = Math.abs(rotateQuarterTurns || 0) % 4
              
              const loader = new THREE.TextureLoader()
              const maps = loadTextureMaps(texture, loader)
              
              applyPBR({
                mat,
                origMat: child.material,
                type: (texture as any)?.material_type || (texture as any)?.materialType || 'wood',
                meshName: child.name,
                maps,
                rotateTurns: turns,
                hasUV2: !!child.geometry.attributes.uv2,
              })
              mat.needsUpdate = true
              child.material = mat
              // Use same repeat unification for all leg types (including bed base)
              unifyRepeatsFromBase(child.material)
            }
          })
        }

        clone.traverse((child: any) => {
          if (child.isMesh) {
            child.castShadow = true
            child.receiveShadow = true
          }
        })

        // For bed bases, position at bed center; for legs, use individual positions
        let groupPosition: [number, number, number]
        if (effectiveLegTypeId === 3) {
          if (isFlyingOptics) {
            groupPosition = [
              position[0] as number + legCenter.x * BASE_SCALE,
              legY as number,
              position[1] as number + legCenter.z * BASE_SCALE
            ]
          } else {
            // Bed base: center under bed, accounting for the clone's local position offset
            const bedCenterX = (bedBox.min.x + bedBox.max.x) / 2
            const bedCenterZ = (bedBox.min.z + bedBox.max.z) / 2
            // Special case: nudge Eicon base towards the top edge similar to Flying Optics top offset.
            const eiconTopOffsetZ = isEiconOnSupremeIconSc26 ? -END_OFFSET : 0
            groupPosition = [bedCenterX + legCenter.x * BASE_SCALE, legY as number, bedCenterZ + legCenter.z * BASE_SCALE + eiconTopOffsetZ]
          }
        } else {
          // Regular legs: use individual positions
          groupPosition = [position[0] as number, legY as number, position[1] as number]
        }
        
        return (
          <group key={i} position={groupPosition as [number, number, number]} rotation={rotations[i] as [number, number, number]}>
            <primitive object={clone} />
          </group>
        )
      })}
    </>
  )
}

const Legs = memo(LegsCmp)
export default Legs


